/* Generated by scripts/build-bies-microevents.mjs. Do not edit directly. */
(function () {
'use strict';
/** Reference transport. The Shopify adapters must supply authorized context and
 * consent; this module never reads cookies, storage, customer IDs or DOM inputs. */
const LIMITS = Object.freeze({
  eventBytes: 2048, batchBytes: 12288, batchEvents: 12,
  queueBytes: 49152, queueEvents: 64, ttlMs: 120000,
});

const TERMINAL = new Set(['cart_exit_observed']);
const ALLOWED = new Set([
  'page_observed', 'cart_state_observed', 'cart_exit_observed',
  'cart_reward_exposed', 'cart_reward_eligibility_changed', 'cart_reward_reconciled',
  'incentive_offer_exposed', 'incentive_offer_selected',
  'incentive_offer_clicked', 'incentive_offer_dismissed',
]);
const CART_KEYS = ['cart_instance_id', 'cart_version', 'currency', 'subtotal_minor',
  'items_count', 'distinct_lines_count', 'lines_truncated', 'lines',
  'pending_mutations', 'checkout_state', 'cart_token_hash'];
const REWARD_COMMON_KEYS = ['placement', 'visibility', 'state', 'cart_instance_id',
  'cart_version', 'reward_id', 'eligibility'];
const OFFER_COMMON_KEYS = ['placement', 'visibility', 'state', 'cart_instance_id',
  'cart_version', 'offer_id', 'offer_version', 'threshold_minor', 'currency',
  'gift_variant_id', 'exposure_id'];
const PROPERTY_KEYS = {
  page_observed: ['measurement_start'],
  cart_state_observed: CART_KEYS,
  cart_exit_observed: [...CART_KEYS, 'exit_episode_id', 'reason'],
  cart_reward_exposed: [...REWARD_COMMON_KEYS, 'exposure_id', 'visible_ms', 'active_ms', 'coverage_bps'],
  cart_reward_eligibility_changed: REWARD_COMMON_KEYS,
  cart_reward_reconciled: [...REWARD_COMMON_KEYS, 'reconciliation_source'],
  incentive_offer_exposed: [...OFFER_COMMON_KEYS, 'visible_ms', 'active_ms', 'coverage_bps'],
  incentive_offer_selected: OFFER_COMMON_KEYS,
  incentive_offer_clicked: OFFER_COMMON_KEYS,
  incentive_offer_dismissed: OFFER_COMMON_KEYS,
};
const CONTEXT_KEYS = ['session_id', 'visitor_id', 'identity_epoch', 'page_type', 'path',
  'release_id', 'visit_channel', 'campaign_id', 'adset_id', 'ad_id', 'fbp', 'fbc'];

const SHARED_PAGEVIEW_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
function sharedPageViewId() {
  try {
    const id = typeof window !== 'undefined' ? window.__bies_pageview_event_id : null;
    return typeof id === 'string' && SHARED_PAGEVIEW_UUID.test(id) ? id : null;
  } catch { return null; }
}

class TokenBucket {
  /** @param {number} perMinute @param {number} capacity @param {number} now */
  constructor(perMinute, capacity, now) {
    this.rate = perMinute / 60000;
    this.capacity = capacity;
    this.tokens = capacity;
    this.updated = now;
  }
  /** @param {number} now @returns {boolean} */
  take(now) {
    this.tokens = Math.min(this.capacity, this.tokens + Math.max(0, now - this.updated) * this.rate);
    this.updated = now;
    if (this.tokens < 1) return false;
    this.tokens -= 1;
    return true;
  }
}

class MicroTransport {
  /** Dependencies are explicit for browser integration and deterministic tests.
   * @param {{endpoint:string, consent:Function, context:Function, uuid:Function,
   * now:Function, isoNow:Function, visible:Function, beacon:Function, fetch:Function}} deps
   */
  constructor(deps) {
    this.deps = deps;
    this.encoder = new TextEncoder();
    this.queue = [];
    this.pageViewId = null;
    this.sequence = 0;
    this.prepared = null;
    this.inflight = null;
    this.hiddenAttempted = false;
    this.counters = { dropped_events: 0, beacon_rejected: 0, ack_failed: 0 };
    this.ordinary = new TokenBucket(30, 10, deps.now());
    this.terminal = new TokenBucket(12, 4, deps.now());
    this.hiddenBudget = new TokenBucket(6, 2, deps.now());
  }

  /** Call after any permission change. Conservative: purge on every change. */
  permissionChanged() {
    this.queue = [];
    this.prepared = null;
    this.inflight?.controller.abort();
    this.pageViewId = null;
    this.sequence = 0;
  }

  /** Snapshot small, schema-allowlisted properties after the commercial handler.
   * Never pass an event target, form object, error message or user input.
   * @param {string} name @param {object} properties @param {string|null} coalesceKey
   * @returns {boolean} Local admission only, never delivery confirmation.
   */
  track(name, properties, coalesceKey = null) {
    try {
      const consent = this.deps.consent();
      if (!consent?.analytics || !ALLOWED.has(name)) return false;
      if (Object.keys(properties).some(key => !PROPERTY_KEYS[name].includes(key)) ||
          Object.entries(properties).some(([key, value]) => key !== 'lines' && value !== null && typeof value === 'object') ||
          (properties.lines && (!Array.isArray(properties.lines) || properties.lines.some(line =>
            Object.keys(line).some(key => !['variant_id', 'quantity', 'role'].includes(key)) ||
            Object.values(line).some(value => typeof value === 'object'))))) return this.drop();
      this.expire();
      const priority = TERMINAL.has(name) ? 1 : 0;
      const bucket = priority ? this.terminal : this.ordinary;
      if (!bucket.take(this.deps.now())) return this.drop();
      // spec bies-theme-rendimiento-cookies-y-senal-v1 task 05: share the page-level
      // event id minted synchronously in theme.liquid (window.__bies_pageview_event_id)
      // so the browser pixel, the Web Pixel sync and this collector agree on one
      // page_view_id. Falls back to a local UUID when the global is absent or invalid.
      this.pageViewId ??= sharedPageViewId() || this.deps.uuid();
      const context = this.deps.context();
      if (Object.keys(context).some(key => !CONTEXT_KEYS.includes(key)) ||
          Object.values(context).some(value => value !== null && typeof value === 'object') ||
          Object.keys(consent).some(key => !['analytics', 'marketing', 'customer_linking', 'revision'].includes(key))) return this.drop();
      const event = {
        event_id: this.deps.uuid(), event_name: name,
        occurred_at: this.deps.isoNow(), event_seq: ++this.sequence,
        page_view_id: this.pageViewId,
        context: { ...context, consent: { ...consent },
          fbp: consent.marketing ? context.fbp : null,
          fbc: consent.marketing ? context.fbc : null },
        properties,
      };
      const encoded = JSON.stringify(event);
      const bytes = this.encoder.encode(encoded).byteLength;
      if (bytes > LIMITS.eventBytes) return this.drop();
      // Coalescing can replace only unsent observations. Already offered events
      // retain immutable IDs/content, including unacknowledged beacon records.
      if (coalesceKey) this.queue = this.queue.filter(row =>
        row.key !== coalesceKey || row.offered);
      while (this.queue.length >= LIMITS.queueEvents ||
             this.queue.reduce((sum, row) => sum + row.bytes, 0) + bytes > LIMITS.queueBytes) {
        const victim = this.queue.findIndex(row => row.priority <= priority);
        if (victim < 0) return this.drop();
        this.queue.splice(victim, 1);
        this.counters.dropped_events++;
      }
      this.queue.push({ id: event.event_id, encoded, bytes, priority,
        key: coalesceKey, offered: false, attempts: 0, nextAt: 0, born: this.deps.now() });
      this.prepare();
      return true;
    } catch {
      // Instrumentation cannot break product interactions.
      return this.drop();
    }
  }

  /** @returns {false} */
  drop() { this.counters.dropped_events++; return false; }

  expire() {
    const previous = this.queue.length;
    this.queue = this.queue.filter(row => this.deps.now() - row.born < LIMITS.ttlMs && row.attempts < 3);
    this.counters.dropped_events += previous - this.queue.length;
  }

  /** Build a cached small body outside input/exit work whenever possible.
   * @param {boolean} eligibleOnly @returns {{body:string,rows:Array}|null}
   */
  prepare(eligibleOnly = false) {
    const prefix = JSON.stringify({ action: 'micro_events', schema_version: 1,
      batch_id: this.deps.uuid(), sent_at: this.deps.isoNow() }).slice(0, -1) + ',"events":[';
    let bytes = this.encoder.encode(prefix + ']}').byteLength;
    const rows = [];
    // Stable priority sorting preserves oldest-first within each class.
    for (const row of [...this.queue].sort((a, b) => b.priority - a.priority)) {
      if (eligibleOnly && row.nextAt > this.deps.now()) continue;
      const extra = row.bytes + (rows.length ? 1 : 0);
      if (rows.length >= LIMITS.batchEvents || bytes + extra > LIMITS.batchBytes) break;
      rows.push(row);
      bytes += extra;
    }
    const batch = rows.length ? { body: prefix + rows.map(row => row.encoded).join(',') + ']}', rows } : null;
    if (!eligibleOnly) this.prepared = batch;
    return batch;
  }

  /** Ordinary visible-page delivery with explicit ACK and bounded retries. */
  async flushVisible() {
    if (!this.deps.consent()?.analytics || !this.deps.visible() || this.inflight) return;
    this.expire();
    const batch = this.prepare(true);
    if (!batch) return;
    const controller = new AbortController();
    const request = { controller };
    this.inflight = request;
    batch.rows.forEach(row => { row.offered = true; row.attempts++; });
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await this.deps.fetch(this.deps.endpoint, {
        method: 'POST', credentials: 'omit', mode: 'cors', keepalive: false,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: batch.body, signal: controller.signal,
      });
      const ack = await response.json();
      if (response.status === 409) {
        if (!Array.isArray(ack.accepted_event_ids) || !Array.isArray(ack.rejected_event_ids)) {
          throw new Error('invalid_conflict_ack');
        }
        const batchIds = new Set(batch.rows.map(row => row.id));
        const accepted = new Set(ack.accepted_event_ids.filter(id => batchIds.has(id)));
        const rejected = new Set(ack.rejected_event_ids.filter(id => batchIds.has(id)));
        this.queue = this.queue.filter(row => !accepted.has(row.id) && !rejected.has(row.id));
        this.counters.dropped_events += rejected.size;
      } else {
        if (!response.ok || !Array.isArray(ack.accepted_event_ids) ||
            !batch.rows.every(row => ack.accepted_event_ids.includes(row.id))) throw new Error('incomplete_ack');
        const accepted = new Set(batch.rows.map(row => row.id));
        this.queue = this.queue.filter(row => !accepted.has(row.id));
      }
    } catch {
      this.counters.ack_failed++;
      batch.rows.forEach(row => { row.nextAt = this.deps.now() + Math.min(60000, 10000 * 2 ** (row.attempts - 1)); });
    } finally {
      clearTimeout(timer);
      if (this.inflight === request) this.inflight = null;
      this.prepare();
    }
  }

  /** One offer per hidden episode; beforeHide only reads cached UI state.
   * @param {Function} beforeHide @returns {boolean}
   */
  flushHidden(beforeHide = () => {}) {
    if (this.hiddenAttempted || !this.deps.consent()?.analytics) return false;
    this.hiddenAttempted = true;
    if (!this.hiddenBudget.take(this.deps.now())) return this.drop();
    try { beforeHide(); } catch { this.counters.dropped_events++; }
    this.expire();
    const batch = this.prepare();
    if (!batch) return false;
    let queued = false;
    try {
      queued = this.deps.beacon(this.deps.endpoint, new Blob([batch.body], { type: 'text/plain;charset=UTF-8' }));
    } catch { /* Rejection follows the same bounded recovery path. */ }
    if (queued) batch.rows.forEach(row => { row.offered = true; });
    else this.counters.beacon_rejected++;
    return queued;
  }

  resume() { this.hiddenAttempted = false; this.expire(); this.prepare(); }
}

/** Wire once, after privacy initialization, in the theme's authorized adapter.
 * beforeHide freezes exposure clocks and emits the cached confirmed cart snapshot.
 * @param {MicroTransport} transport @param {Function} beforeHide
 * @returns {Function} cleanup
 */
function wireLifecycle(transport, beforeHide) {
  const visibility = () => {
    if (document.visibilityState === 'hidden') transport.flushHidden(() => beforeHide('visibility_hidden'));
    else { transport.resume(); void transport.flushVisible(); }
  };
  const pagehide = () => transport.flushHidden(() => beforeHide('pagehide'));
  const pageshow = () => { if (document.visibilityState === 'visible') transport.resume(); };
  document.addEventListener('visibilitychange', visibility);
  window.addEventListener('pagehide', pagehide);
  window.addEventListener('pageshow', pageshow);
  const timer = setInterval(() => void transport.flushVisible(), 10000);
  return () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', visibility);
    window.removeEventListener('pagehide', pagehide);
    window.removeEventListener('pageshow', pageshow);
    transport.permissionChanged();
  };
}


/** Pure exposure clock. DOM adapter supplies observer geometry, document
 * visibility and known-overlay occlusion. No conclusion about actual reading. */
class ExposureClock {
  /** @param {number} now performance.now(), never wall-clock time */
  constructor(now) {
    this.last = now;
    this.activeUntil = now + 30000;
    this.visible = false;
    this.visibleMs = 0;
    this.activeMs = 0;
    this.covered = new Set();
  }
  /** @param {number} now */
  advance(now) {
    const end = Math.max(now, this.last);
    if (this.visible) {
      this.visibleMs += end - this.last;
      this.activeMs += Math.max(0, Math.min(end, this.activeUntil) - this.last);
    }
    this.last = end;
  }
  /** @param {number} now @param {boolean} isVisible */
  setVisible(now, isVisible) { this.advance(now); this.visible = isVisible; }
  /** Invoke for trusted pointer/key/scroll activity while the page is visible.
   * @param {number} now */
  activity(now) { this.advance(now); this.activeUntil = now + 30000; }
  /** Mark only vertical bins actually visible; a jump does not fill the gap.
   * @param {number} from normalized [0,1] @param {number} to normalized [0,1] */
  cover(from, to) {
    if (!this.visible) return;
    for (let bin = 0; bin < 20; bin++) {
      if ((bin + 0.5) / 20 >= Math.max(0, from) &&
          (bin + 0.5) / 20 <= Math.min(1, to)) this.covered.add(bin);
    }
  }
  /** Cumulative values; SQL reduces checkpoints with MAX, never SUM.
   * @param {number} now @returns {{visible_ms:number,active_ms:number,coverage_bps:number}} */
  snapshot(now) {
    this.advance(now);
    return { visible_ms: Math.round(this.visibleMs), active_ms: Math.round(this.activeMs),
      coverage_bps: this.covered.size * 500 };
  }
}

/** IntersectionObserver callback helper; excludes known modal occlusion.
 * rootBounds may be null: supply cached viewport height from a resize listener.
 * @param {IntersectionObserverEntry} entry @param {number} viewportHeight
 * @param {boolean} documentVisible @param {boolean} overlayOpen
 * @returns {boolean} */
function qualifiesGeometry(entry, viewportHeight, documentVisible, overlayOpen) {
  if (!documentVisible || overlayOpen || !entry.isIntersecting) return false;
  const height = Math.min(entry.boundingClientRect.height, viewportHeight);
  const width = Math.min(entry.boundingClientRect.width, entry.rootBounds?.width ?? entry.boundingClientRect.width);
  return height > 0 && width > 0 && entry.intersectionRect.height >= height * 0.5 &&
    entry.intersectionRect.width >= width * 0.5;
}


/**
 * BIES storefront adapter. This file is intentionally small: it translates only
 * authorized storefront facts to the shared transport contract. It never turns
 * cart confirmation, a click, or a DOM presence into an exposure by itself.
 */
const COLLECT_ENDPOINT = 'https://collect-2dwmk4g42a-ew.a.run.app/collect';
const ALLOWED_PATHS = [/^\/$/, /^\/products\/[A-Za-z0-9-]+$/, /^\/collections\/[A-Za-z0-9-]+$/,
  /^\/cart$/, /^\/pages\/[A-Za-z0-9-]+$/];
const PAGE_TYPES = new Set(['home', 'product', 'collection', 'cart', 'page', 'other']);
const PLACEMENTS = new Set(['cart_drawer', 'cart_page', 'product_volume', 'home_needs_pack', 'custom_pack']);
let cartState = null;
let memorySessionId = null;
let transport = null;
let lifecycleCleanup = null;
let cartGeneration = 0;
let cartVersion = 0;
let privacyIntentEpoch = 0;
const exposureBindings = new Map();
const offerClickListeners = new WeakMap();

function safeUuid() {
  if (!window.crypto?.getRandomValues && !window.crypto?.randomUUID) return null;
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  return [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('')
    .replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5');
}

function consent() {
  try {
    if (window._biesConsentState !== 'granted' ||
        window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return null;
    // Linking is a separate, deliberately fail-closed purpose. The trusted
    // provider must affirm it and supply both opaque identifiers; analytics
    // consent or either identifier alone can never enable linking.
    return { analytics: true, marketing: false,
      customer_linking: identityContext().customer_linking, revision: 'bies-v1' };
  } catch { return null; }
}

function allowedPath() {
  const path = window.location.pathname;
  return ALLOWED_PATHS.some(rule => rule.test(path)) ? path : '/';
}

function pageType() {
  const template = document.body?.dataset?.template?.split('.')[0];
  if (PAGE_TYPES.has(template)) return template;
  if (allowedPath() === '/') return 'home';
  return 'other';
}

function sessionId() {
  // Reuse the canonical session namespace. This adapter never creates a second
  // identifier in storage; the volatile fallback remains stable for this page.
  try {
    const value = window.SessionTracker?.sessionId || sessionStorage.getItem('_bies_session_id');
    if (value) return value;
  } catch { /* Storage can be denied independently of analytics consent. */ }
  memorySessionId ??= safeUuid();
  return memorySessionId;
}

function identityContext() {
  const provider = window.BIES_IDENTITY_CONTEXT || window.BiesIdentityContext || {};
  const valid = value => typeof value === 'string' && /^[A-Za-z0-9_.:-]{1,128}$/.test(value);
  const visitor_id = valid(provider.visitor_id) ? provider.visitor_id : null;
  const identity_epoch = valid(provider.identity_epoch) ? provider.identity_epoch : null;
  return {
    visitor_id, identity_epoch,
    customer_linking: provider.customer_linking === true && visitor_id !== null && identity_epoch !== null,
  };
}

async function cartTokenHash(token) {
  if (!token || !window.crypto?.subtle) return null;
  try {
    const normalized = String(token);
    const bytes = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalized));
    return [...new Uint8Array(bytes)].map(byte => byte.toString(16).padStart(2, '0')).join('');
  } catch { return null; }
}

function cartEvidenceShape(cart) {
  if (!Array.isArray(cart?.items) || !cart.items.every(item => typeof item.key === 'string')) return null;
  return JSON.stringify([cart.currency, cart.total_price, cart.item_count,
    cart.items.map(item => [item.key, item.variant_id, item.quantity, item.final_line_price])
      .sort((left, right) => left[0].localeCompare(right[0]))]);
}

async function resolveCartTokenHash(cart) {
  if (cart.token) return cartTokenHash(cart.token);
  // Liquid's cart JSON omits the Ajax cart token. Read it only with consent,
  // and bind it only when the full line-state still matches the confirmed cart.
  const expected = cartEvidenceShape(cart);
  if (!expected || !consent()) return null;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 1500);
  try {
    const response = await window.fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
    });
    if (!response.ok || !consent()) return null;
    const current = await response.json();
    if (cartEvidenceShape(current) !== expected) return null;
    return await cartTokenHash(current.token);
  } catch { return null; }
  finally { clearTimeout(timeout); }
}

function authorizedContext() {
  const permission = consent();
  if (!permission) return null;
  return {
    session_id: sessionId(),
    visitor_id: identityContext().visitor_id, identity_epoch: identityContext().identity_epoch,
    page_type: pageType(), path: allowedPath(),
    release_id: window.BIES_THEME_RELEASE_ID || 'theme-liquid', visit_channel: 'unknown',
    campaign_id: null, adset_id: null, ad_id: null, fbp: null, fbc: null,
  };
}

function ensureTransport() {
  if (transport || !consent()) return transport;
  transport = new MicroTransport({
    endpoint: COLLECT_ENDPOINT, consent, context: authorizedContext, uuid: safeUuid,
    now: () => Date.now(), isoNow: () => new Date().toISOString(),
    visible: () => document.visibilityState === 'visible',
    beacon: (url, body) => navigator.sendBeacon(url, body), fetch: (...args) => window.fetch(...args),
  });
  lifecycleCleanup = wireLifecycle(transport, reason => freezeExposures(reason));
  transport.track('page_observed', { measurement_start: 'navigation' });
  void transport.flushVisible();
  return transport;
}

function resetTransport() {
  lifecycleCleanup?.();
  lifecycleCleanup = null;
  transport?.permissionChanged();
  transport = null;
  cartState = null;
  cartGeneration++;
  cartVersion = 0;
  exposureBindings.forEach(binding => binding.stop());
  exposureBindings.clear();
}

function cartSnapshot(cart, previous = cartState) {
  const giftIds = new Set((previous?.rewardTiers || []).map(tier => String(tier.giftVariantId)));
  const lines = Array.isArray(cart.items) ? cart.items.slice(0, 8).map(item => ({
    variant_id: String(item.variant_id), quantity: Number(item.quantity),
    role: giftIds.has(String(item.variant_id)) ? 'gift' : 'merchandise',
  })) : [];
  return {
    cart_instance_id: previous?.cart_instance_id || safeUuid(),
    cart_version: ++cartVersion,
    currency: String(cart.currency || 'EUR').toUpperCase(),
    subtotal_minor: Math.max(0, Number(cart.total_price) || 0),
    items_count: Math.max(0, Number(cart.item_count) || 0),
    distinct_lines_count: Array.isArray(cart.items) ? cart.items.length : 0,
    lines_truncated: Array.isArray(cart.items) && cart.items.length > 8,
    lines, pending_mutations: 0, checkout_state: 'not_started',
  };
}

function offerProperties(element, state, exposureId = null, clock = null) {
  const placement = element.dataset.biesOfferPlacement;
  const offerId = element.dataset.biesOfferId;
  const offerVersion = element.dataset.biesOfferVersion;
  if (!PLACEMENTS.has(placement) || !offerId || !offerVersion) return null;
  const properties = {
    placement, visibility: state === 'exposed' ? 'visible' : 'not_visible', state,
    cart_instance_id: cartState?.cart_instance_id ?? null,
    cart_version: cartState?.cart_version ?? null,
    offer_id: offerId, offer_version: offerVersion, exposure_id: exposureId,
  };
  if (state === 'exposed') Object.assign(properties, clock.snapshot(performance.now()));
  return properties;
}

function rewardProperties(rewardId, clock) {
  if (!cartState) return null;
  const tier = cartState.rewardTiers.find(value => String(value.giftVariantId) === rewardId);
  const eligibility = tier && cartState.subtotal_minor >= Number(tier.threshold)
    ? 'eligible' : 'ineligible';
  return {
    placement: 'cart_drawer', visibility: 'visible', state: 'exposed',
    cart_instance_id: cartState.cart_instance_id, cart_version: cartState.cart_version,
    reward_id: rewardId, eligibility, exposure_id: safeUuid(), ...clock.snapshot(performance.now()),
  };
}

class StorefrontExposure {
  constructor(element, onExpose) {
    this.element = element;
    this.onExpose = onExpose;
    this.clock = new ExposureClock(performance.now());
    this.exposureId = null;
    this.emitted = false;
    this.observer = new IntersectionObserver(entries => entries.forEach(entry => this.observe(entry)),
      { threshold: [0, 0.5, 1] });
    this.observer.observe(element);
    // IntersectionObserver does not fire merely because a modal becomes visible.
    // Watch the DOM and scroll position so a newly active overlay closes the
    // exposure interval at the time it actually obscures the offer.
    this.refreshOnViewportChange = () => this.refresh();
    window.addEventListener('scroll', this.refreshOnViewportChange, true);
    this.mutations = new MutationObserver(() => this.refresh());
    this.mutations.observe(document.documentElement || document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'style', 'open', 'hidden', 'aria-hidden'],
    });
  }
  activeOverlay() {
    const overlays = document.querySelectorAll?.(
      '.modal[open], dialog[open], [role="dialog"][aria-modal="true"], [aria-modal="true"][data-bies-overlay-active="true"]'
    ) || [];
    return [...overlays].some(overlay => {
      if (overlay === this.element || overlay.contains?.(this.element)) return false;
      const style = window.getComputedStyle?.(overlay);
      if (style && (style.display === 'none' || style.visibility === 'hidden' ||
          style.visibility === 'collapse' || Number(style.opacity) === 0)) return false;
      const rect = overlay.getBoundingClientRect?.();
      const right = rect?.right ?? (rect?.left ?? 0) + (rect?.width ?? 0);
      const bottom = rect?.bottom ?? (rect?.top ?? 0) + (rect?.height ?? 0);
      return Boolean(rect && right > 0 && bottom > 0 && rect.left < window.innerWidth && rect.top < window.innerHeight);
    });
  }
  covered() {
    if (this.activeOverlay()) return true;
    const rect = this.element.getBoundingClientRect?.();
    const top = document.elementFromPoint?.(rect?.left + rect?.width / 2, rect?.top + rect?.height / 2);
    return Boolean(top && top !== this.element && !this.element.contains?.(top));
  }
  observe(entry) {
    const now = performance.now();
    const visible = qualifiesGeometry(entry, window.innerHeight, document.visibilityState === 'visible', this.covered());
    const wasVisible = this.clock.visible;
    this.clock.setVisible(now, visible);
    if (visible) {
      this.exposureId ??= safeUuid();
      const top = Math.max(0, entry.intersectionRect.top - entry.boundingClientRect.top) / Math.max(1, entry.boundingClientRect.height);
      const bottom = Math.min(entry.boundingClientRect.height, entry.intersectionRect.bottom - entry.boundingClientRect.top) / Math.max(1, entry.boundingClientRect.height);
      this.clock.cover(top, bottom);
    }
    if (wasVisible && !visible) this.emit();
  }
  refresh() {
    const rect = this.element.getBoundingClientRect?.();
    if (!rect) return;
    const viewportWidth = window.innerWidth || rect.width;
    const viewportHeight = window.innerHeight || rect.height;
    const right = rect.right ?? rect.left + rect.width;
    const bottom = rect.bottom ?? rect.top + rect.height;
    const left = Math.max(0, rect.left);
    const top = Math.max(0, rect.top);
    const clippedRight = Math.min(viewportWidth, right);
    const clippedBottom = Math.min(viewportHeight, bottom);
    const width = Math.max(0, clippedRight - left);
    const height = Math.max(0, clippedBottom - top);
    this.observe({ isIntersecting: width > 0 && height > 0,
      boundingClientRect: rect,
      intersectionRect: { left, top, right: clippedRight, bottom: clippedBottom, width, height },
      rootBounds: { width: viewportWidth, height: viewportHeight } });
  }
  activity() { this.clock.activity(performance.now()); }
  freeze() { this.clock.setVisible(performance.now(), false); this.emit(); }
  emit() {
    if (this.emitted || !this.exposureId || this.clock.snapshot(performance.now()).visible_ms === 0) return;
    this.emitted = true;
    this.onExpose(this.clock, this.exposureId);
  }
  stop() {
    this.freeze();
    this.observer.disconnect();
    this.mutations.disconnect();
    window.removeEventListener('scroll', this.refreshOnViewportChange, true);
  }
}

function bindExposure(key, element, callback) {
  const existing = exposureBindings.get(key);
  if (existing?.element === element) return existing;
  if (existing) { existing.stop(); exposureBindings.delete(key); }
  const binding = new StorefrontExposure(element, callback);
  exposureBindings.set(key, binding);
  return binding;
}

function bindOffers() {
  const activeTransport = ensureTransport();
  if (!activeTransport) return;
  document.querySelectorAll('[data-bies-offer-id][data-bies-offer-version]').forEach(element => {
    const key = `offer:${element.dataset.biesOfferPlacement}:${element.dataset.biesOfferId}:${element.dataset.biesOfferVersion}`;
    bindExposure(key, element, (clock, exposureId) => {
      const properties = offerProperties(element, 'exposed', exposureId, clock);
      if (properties) activeTransport.track('incentive_offer_exposed', properties);
    });
    // A DOM node can survive cart redraws and consent regrants. Install its
    // listener once, then resolve the live binding and transport at click time.
    if (offerClickListeners.has(element)) return;
    const click = event => {
      // A label click forwards a second, zero-detail click to its radio input.
      // The selection hook records keyboard changes; this forwarded click is
      // not another offer response.
      if (event?.detail === 0 && event.target?.matches?.('input[type="radio"]')) return;
      const liveTransport = ensureTransport();
      const liveBinding = exposureBindings.get(key);
      const properties = offerProperties(element, 'clicked',
        liveBinding?.emitted ? liveBinding.exposureId : null);
      if (properties && liveTransport) {
        liveTransport.track('incentive_offer_clicked', properties);
        void liveTransport.flushVisible();
      }
    };
    element.addEventListener('click', click);
    offerClickListeners.set(element, click);
  });
}

function bindRewards(drawerOpen) {
  if (!drawerOpen || !cartState || !transport) return;
  document.querySelectorAll('[data-bies-reward-id]').forEach(element => {
    const key = `reward:${cartState.cart_version}:${element.dataset.biesRewardId}`;
    const snapshot = {...cartState};
    bindExposure(key, element, (clock, exposureId) => {
      const tier = snapshot.rewardTiers.find(value => String(value.giftVariantId) === element.dataset.biesRewardId);
      const properties = tier ? { placement: 'cart_drawer', visibility: 'visible', state: 'exposed',
        cart_instance_id: snapshot.cart_instance_id, cart_version: snapshot.cart_version,
        reward_id: element.dataset.biesRewardId,
        eligibility: snapshot.subtotal_minor >= Number(tier.threshold) ? 'eligible' : 'ineligible',
        exposure_id: exposureId, ...clock.snapshot(performance.now()) } : null;
      if (properties) transport?.track('cart_reward_exposed', properties);
    });
  });
}

async function recordConfirmedCart(detail) {
  const activeTransport = ensureTransport();
  if (!activeTransport || !detail?.cart) return;
  const generation = ++cartGeneration;
  const previousCartState = cartState;
  exposureBindings.forEach(binding => binding.stop());
  exposureBindings.clear();
  const previousTiers = Array.isArray(detail.rewardTiers) ? detail.rewardTiers : [];
  const snapshot = cartSnapshot(detail.cart, {...previousCartState, rewardTiers: previousTiers});
  const cart_token_hash = await resolveCartTokenHash(detail.cart);
  if (generation !== cartGeneration || transport !== activeTransport || !consent() || !snapshot.cart_instance_id) return;
  if (cart_token_hash) snapshot.cart_token_hash = cart_token_hash;
  cartState = { ...snapshot, rewardTiers: previousTiers };
  activeTransport.track('cart_state_observed', snapshot, 'cart-state');
  for (const tier of cartState.rewardTiers) {
    const rewardId = String(tier.giftVariantId);
    const eligible = snapshot.subtotal_minor >= Number(tier.threshold) ? 'eligible' : 'ineligible';
    activeTransport.track('cart_reward_eligibility_changed', {
      placement: 'cart_drawer', visibility: detail.drawerOpen ? 'visible' : 'not_visible',
      state: 'eligibility_changed', cart_instance_id: snapshot.cart_instance_id,
      cart_version: snapshot.cart_version, reward_id: rewardId, eligibility: eligible,
    });
    if (snapshot.lines.some(line => line.variant_id === rewardId && line.role === 'gift')) {
      activeTransport.track('cart_reward_reconciled', {
        placement: 'cart_drawer', visibility: detail.drawerOpen ? 'visible' : 'not_visible',
        state: 'reconciled', cart_instance_id: snapshot.cart_instance_id,
        cart_version: snapshot.cart_version, reward_id: rewardId, eligibility: eligible,
        reconciliation_source: 'shopify',
      });
    }
  }
  bindRewards(Boolean(detail.drawerOpen));
  bindOffers();
  void activeTransport.flushVisible();
}

function freezeExposures(reason) {
  exposureBindings.forEach(binding => binding.freeze());
  if (cartState && transport) {
    const { rewardTiers, ...snapshot } = cartState;
    transport.track('cart_exit_observed', { ...snapshot, exit_episode_id: safeUuid(), reason });
  }
}

document.addEventListener('bies:cart:confirmed', event => recordConfirmedCart(event.detail));
document.addEventListener('bies:volume:selected', event => {
  const detail = event.detail || {};
  const activeTransport = ensureTransport();
  if (!activeTransport || !detail.offer_id || !detail.offer_version) return;
  activeTransport.track('incentive_offer_selected', {
    placement: 'product_volume', visibility: 'not_visible', state: 'selected',
    cart_instance_id: cartState?.cart_instance_id ?? null, cart_version: cartState?.cart_version ?? null,
    offer_id: String(detail.offer_id), offer_version: String(detail.offer_version), exposure_id: null,
  });
  void activeTransport.flushVisible();
});
function reconcilePrivacyConsent(intentEpoch, attemptsLeft = 3) {
  if (intentEpoch !== privacyIntentEpoch) return;
  if (consent()) { ensureTransport(); bindOffers(); return; }
  // Shopify updates analyticsProcessingAllowed asynchronously after the banner
  // records intent. Retry only for the same intent and still require both gates.
  if (attemptsLeft > 0) setTimeout(() => reconcilePrivacyConsent(intentEpoch, attemptsLeft - 1), 250);
}

window.addEventListener('bies:consentchange', event => {
  const intentEpoch = ++privacyIntentEpoch;
  if (event.detail?.analytics === true) reconcilePrivacyConsent(intentEpoch);
  else resetTransport();
});
// Official Shopify privacy completion event. A late grant can never revive a
// later revoke because reconciliation re-checks both gates and intent epoch.
document.addEventListener('visitorConsentCollected', () => reconcilePrivacyConsent(privacyIntentEpoch, 0));
document.addEventListener('DOMContentLoaded', bindOffers);
['pointerdown', 'keydown', 'scroll'].forEach(name => window.addEventListener(name, () => exposureBindings.forEach(binding => binding.activity()), {passive: true}));
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') exposureBindings.forEach(binding => binding.freeze());
  else exposureBindings.forEach(binding => binding.refresh());
});
if (consent()) { ensureTransport(); bindOffers(); }
window.BiesMicroevents = { bindOffers, recordConfirmedCart, resetTransport,
  get transport() { return transport; }, get exposures() { return exposureBindings; } };

}());
